import axios from "axios";

/**
 * Statement Dashboard API hook — admin dashboard over the deferred
 * statement-of-result pipeline.
 *
 * Base path: /api/statement-dashboard (axios baseURL already ends with /api).
 * Roles: REGISTRAR, SECRETARY_SPGS, DEAN_SPGS, ADMIN.
 */
const BASE = "statement-dashboard";

const StatementDashboard = {
    /**
     * Dashboard snapshot: per-status task counts, statement total, latest bulk-init report.
     * @param {object} [params]
     * @param {number} [params.sessionId] scopes counts + total to a session (global if omitted)
     * @param {string} [params.type] STATEMENT (default) | CERTIFICATE
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    overview: async ({ sessionId, type } = {}) => {
        const query = {};
        if (sessionId != null) query.sessionId = sessionId;
        if (type != null) query.type = type;
        return axios.get(`${BASE}/overview`, { params: query });
    },

    /**
     * Paginated, filterable task list (Spring Data page).
     * @param {object} [params]
     * @param {string} [params.status] task status filter
     * @param {number} [params.sessionId] session filter
     * @param {number} [params.page] default 0
     * @param {number} [params.size] default 20
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    tasks: async ({ status, sessionId, page = 0, size = 20 } = {}) => {
        const query = { page, size };
        if (status != null) query.status = status;
        if (sessionId != null) query.sessionId = sessionId;
        return axios.get(`${BASE}/tasks`, { params: query });
    },

    /**
     * Single task by id. 404 if not found.
     * @param {number} id
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    task: async (id) => {
        return axios.get(`${BASE}/tasks/${id}`);
    },

    /**
     * Retry a FAILED task (FAILED -> PENDING). 400 if not FAILED, 404 if not found.
     * @param {number} id
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    retry: async (id) => {
        return axios.post(`${BASE}/tasks/${id}/retry`);
    },

    /**
     * Cancel a PENDING task (PENDING -> CANCELLED). 400 if not PENDING, 404 if not found.
     * @param {number} id
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    cancel: async (id) => {
        return axios.post(`${BASE}/tasks/${id}/cancel`);
    },

    /**
     * Manually queue creation for a student+session (runs immediately). Idempotent:
     * if a statement already exists, the response body is empty (200).
     * @param {object} data
     * @param {string} data.matric
     * @param {number} data.sessionId
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    enqueue: async ({ matric, sessionId }) => {
        return axios.post(`${BASE}/tasks/enqueue`, { matric, sessionId });
    },
};

export default StatementDashboard;
